import { memo, useCallback, useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Button,
  Textarea,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Badge,
  Separator,
} from '@flote/ui';
import { Loader2, User, UserMinus, Users } from 'lucide-react';
import type { Driver, Team } from '@flote/contracts/http';
import type { DeliveryTask, DriverStatus } from '@flote/types/delivery';

const DRIVER_STATUS_BADGES: Record<DriverStatus, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
  off_duty: { label: 'Off Duty', variant: 'outline' },
  idle: { label: 'Idle', variant: 'secondary' },
  active: { label: 'Active', variant: 'default' },
};

interface TaskEditModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  task: DeliveryTask;
  teams: Team[];
  teamsLoading?: boolean;
  drivers: Driver[];
  driversLoading?: boolean;
  onUpdateNotes: (notes: string) => Promise<void>;
  onUpdateTeam: (teamId: string) => Promise<void>;
  onAssignDriver: (driverId: string) => Promise<void>;
  onUnassignDriver: () => Promise<void>;
  isUpdating?: boolean;
  isAssigning?: boolean;
  isUnassigning?: boolean;
}

export const TaskEditModal = memo<TaskEditModalProps>(({
  open,
  onOpenChange,
  task,
  teams,
  teamsLoading = false,
  drivers,
  driversLoading = false,
  onUpdateNotes,
  onUpdateTeam,
  onAssignDriver,
  onUnassignDriver,
  isUpdating = false,
  isAssigning = false,
  isUnassigning = false,
}) => {
  const [notes, setNotes] = useState(task.notes || '');
  const [selectedTeamId, setSelectedTeamId] = useState<string | undefined>(task.teamId);
  const [selectedDriverId, setSelectedDriverId] = useState<string | undefined>(task.driver?.id);
  const [hasNotesChanged, setHasNotesChanged] = useState(false);

  const isProcessing = isUpdating || isAssigning || isUnassigning;
  const hasDriver = !!task.driver?.id;
  const hasTeam = !!task.teamId;
  const canAssign = ['pending', 'assigned'].includes(task.status);
  const canUnassign = task.status === 'assigned' && hasDriver;
  const canChangeTeam = ['pending', 'assigned'].includes(task.status);

  // Reset state when modal opens or task changes
  useEffect(() => {
    if (open) {
      setNotes(task.notes || '');
      setSelectedTeamId(task.teamId);
      setSelectedDriverId(task.driver?.id);
      setHasNotesChanged(false);
    }
  }, [open, task]);

  const handleNotesChange = useCallback((value: string) => {
    setNotes(value);
    setHasNotesChanged(value !== (task.notes || ''));
  }, [task.notes]);

  const handleSaveNotes = useCallback(async () => {
    if (!hasNotesChanged) return;
    await onUpdateNotes(notes);
    setHasNotesChanged(false);
  }, [notes, hasNotesChanged, onUpdateNotes]);

  const handleUpdateTeam = useCallback(async () => {
    if (!selectedTeamId || selectedTeamId === task.teamId) return;
    await onUpdateTeam(selectedTeamId);
  }, [selectedTeamId, task.teamId, onUpdateTeam]);

  const handleAssignDriver = useCallback(async () => {
    if (!selectedDriverId || selectedDriverId === task.driver?.id) return;
    await onAssignDriver(selectedDriverId);
  }, [selectedDriverId, task.driver?.id, onAssignDriver]);

  const handleUnassignDriver = useCallback(async () => {
    await onUnassignDriver();
    setSelectedDriverId(undefined);
  }, [onUnassignDriver]);

  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      if (!newOpen && !isProcessing) {
        onOpenChange(false);
      }
    },
    [onOpenChange, isProcessing]
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit Task</DialogTitle>
          <DialogDescription>
            Update task details and driver assignment for {task.trackingId}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Notes Section */}
          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              placeholder="Add delivery notes..."
              value={notes}
              onChange={(e) => handleNotesChange(e.target.value)}
              rows={3}
            />
            {hasNotesChanged && (
              <Button
                size="sm"
                onClick={handleSaveNotes}
                disabled={isUpdating}
              >
                {isUpdating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Save Notes
              </Button>
            )}
          </div>

          <Separator />

          {/* Team Assignment Section */}
          {canChangeTeam && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-muted-foreground" />
                <Label>Team Assignment</Label>
                {!hasTeam && (
                  <Badge variant="destructive" className="text-xs">Required for unassign</Badge>
                )}
              </div>

              {teamsLoading ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <div className="space-y-2">
                  <Select
                    value={selectedTeamId}
                    onValueChange={setSelectedTeamId}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a team..." />
                    </SelectTrigger>
                    <SelectContent>
                      {teams.length === 0 ? (
                        <div className="p-2 text-sm text-muted-foreground text-center">
                          No teams available
                        </div>
                      ) : (
                        teams.map((team) => (
                          <SelectItem key={team.id} value={team.id}>
                            {team.name}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                  {selectedTeamId && selectedTeamId !== task.teamId && (
                    <Button
                      onClick={handleUpdateTeam}
                      disabled={isUpdating}
                      className="w-full"
                    >
                      {isUpdating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      {hasTeam ? 'Change Team' : 'Assign Team'}
                    </Button>
                  )}
                  <p className="text-xs text-muted-foreground">
                    A team must be assigned before a driver can be unassigned from this task.
                  </p>
                </div>
              )}
            </div>
          )}

          <Separator />

          {/* Driver Assignment Section */}
          {canAssign && (
            <div className="space-y-3">
              <Label>Driver Assignment</Label>

              {/* Current driver info */}
              {hasDriver && task.driver && (
                <div className="flex items-center justify-between rounded-lg border p-3 bg-muted/50">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-background border">
                      <User className="h-5 w-5 text-muted-foreground" />
                    </div>
                    <div>
                      <p className="font-medium">{task.driver.name || 'Assigned Driver'}</p>
                      <p className="text-sm text-muted-foreground">Currently assigned</p>
                    </div>
                  </div>
                  {canUnassign && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleUnassignDriver}
                      disabled={isUnassigning}
                    >
                      {isUnassigning ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <UserMinus className="mr-2 h-4 w-4" />
                      )}
                      Unassign
                    </Button>
                  )}
                </div>
              )}

              {/* Driver selection */}
              <div className="space-y-2">
                <Label htmlFor="driver-select">
                  {hasDriver ? 'Reassign to different driver' : 'Assign driver'}
                </Label>
                {driversLoading ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                  </div>
                ) : (
                  <>
                    <Select
                      value={selectedDriverId}
                      onValueChange={setSelectedDriverId}
                    >
                      <SelectTrigger id="driver-select">
                        <SelectValue placeholder="Select a driver..." />
                      </SelectTrigger>
                      <SelectContent>
                        {drivers.length === 0 ? (
                          <div className="p-2 text-sm text-muted-foreground text-center">
                            No available drivers
                          </div>
                        ) : (
                          drivers.map((driver) => {
                            const badge = DRIVER_STATUS_BADGES[driver.status];
                            return (
                              <SelectItem key={driver.id} value={driver.id}>
                                <div className="flex items-center gap-2">
                                  <span>{driver.name}</span>
                                  <Badge variant={badge.variant} className="text-xs">
                                    {badge.label}
                                  </Badge>
                                </div>
                              </SelectItem>
                            );
                          })
                        )}
                      </SelectContent>
                    </Select>
                    {selectedDriverId && selectedDriverId !== task.driver?.id && (
                      <Button
                        onClick={handleAssignDriver}
                        disabled={isAssigning}
                        className="w-full"
                      >
                        {isAssigning && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        {hasDriver ? 'Reassign Driver' : 'Assign Driver'}
                      </Button>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {!canAssign && (
            <div className="text-sm text-muted-foreground">
              Driver assignment cannot be changed for tasks in &quot;{task.status}&quot; status.
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isProcessing}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
});

TaskEditModal.displayName = 'TaskEditModal';
